import React, { useEffect } from 'react';
import { intlShape } from '../../util/reactIntl';
import { bool, object, string } from 'prop-types';
import config from '../../config';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import { FieldTextInput, FieldSelect, Switch } from '../../components';

import css from './StripePaymentAddress.module.css';

const StripePaymentAddress = (props) => {
  const { className, intl, disabled, form, fieldId, card } = props;

  const [showCompanySection, setShowCompanySection] = React.useState(false);

  const optionalText = intl.formatMessage({
    id: 'StripePaymentAddress.optionalText',
  });

  const addressLine1Label = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Label',
  });
  const addressLine1Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine1Placeholder',
  });
  const addressLine1Required = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.addressLine1Required',
    })
  );

  const addressLine2Label = intl.formatMessage(
    { id: 'StripePaymentAddress.addressLine2Label' },
    { optionalText: optionalText }
  );

  const addressLine2Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.addressLine2Placeholder',
  });

  const postalCodeLabel = intl.formatMessage({ id: 'StripePaymentAddress.postalCodeLabel' });
  const postalCodePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.postalCodePlaceholder',
  });
  const postalCodeRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.postalCodeRequired',
    })
  );

  const cityLabel = intl.formatMessage({ id: 'StripePaymentAddress.cityLabel' });
  const cityPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.cityPlaceholder' });
  const cityRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.cityRequired',
    })
  );

  const stateLabel = intl.formatMessage(
    { id: 'StripePaymentAddress.stateLabel' },
    { optionalText: optionalText }
  );
  const statePlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.statePlaceholder' });

  const countryLabel = intl.formatMessage({ id: 'StripePaymentAddress.countryLabel' });
  const countryPlaceholder = intl.formatMessage({ id: 'StripePaymentAddress.countryPlaceholder' });
  const countryRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.countryRequired',
    })
  );

  const iAmACompanyLabel = intl.formatMessage(
    { id: 'StripePaymentAddress.iAmACompanyLabel' },
    { optionalText: optionalText }
  );

  const companyNameLabel = intl.formatMessage({ id: 'StripePaymentAddress.companyNameLabel' });
  const companyNamePlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.companyNamePlaceholder',
  });
  const companyNameRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.companyNameRequired',
    })
  );

  const vatNumberLabel = intl.formatMessage({ id: 'StripePaymentAddress.vatNumberLabel' });
  const vatNumberPlaceholder = intl.formatMessage({
    id: 'StripePaymentAddress.vatNumberPlaceholder',
  });
  const vatNumberRequired = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.vatNumberRequired',
    })
  );

  const billingAddressLine1Label = intl.formatMessage({ id: 'StripePaymentAddress.billingAddressLine1Label' });
  const billingAddressLine1Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.billingAddressLine1Placeholder',
  });
  const billingAddressLine1Required = validators.required(
    intl.formatMessage({
      id: 'StripePaymentAddress.billingAddressLine1Required',
    })
  );

  const billingAddressLine2Label = intl.formatMessage({ id: 'StripePaymentAddress.billingAddressLine2Label' });
  const billingAddressLine2Placeholder = intl.formatMessage({
    id: 'StripePaymentAddress.billingAddressLine2Placeholder',
  });

  const handleOnChange = (event) => {
    const value = event.target.value;
    const name = event.target.name;
    form.change(name, value);
    card.update({ value: { [name]: value } });
  };

  // Use tha language set in config.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(config.locale);
  const showUSFields = config.locale === 'en';

  useEffect(() => {
    form.change('iAmACompany', showCompanySection);
  }, [showCompanySection])

  return (
    <div className={className ? className : css.root}>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.addressLine1`}
          name="addressLine1"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-line1"
          label={addressLine1Label}
          placeholder={addressLine1Placeholder}
          validate={addressLine1Required}
          onUnmount={() => form.change('addressLine1', undefined)}
        />
        {showUSFields && (
          <FieldTextInput
            id={`${fieldId}.addressLine2`}
            name="addressLine2"
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="billing address-line2"
            label={addressLine2Label}
            placeholder={addressLine2Placeholder}
            onUnmount={() => form.change('addressLine2', undefined)}
          />
        )}
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.postalCode`}
          name="postal"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing postal-code"
          label={postalCodeLabel}
          placeholder={postalCodePlaceholder}
          validate={postalCodeRequired}
          onUnmount={() => form.change('postal', undefined)}
          onChange={(event) => handleOnChange(event)}
        />

        <FieldTextInput
          id={`${fieldId}.city`}
          name="city"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="billing address-level2"
          label={cityLabel}
          placeholder={cityPlaceholder}
          validate={cityRequired}
          onUnmount={() => form.change('city', undefined)}
        />
      </div>
      <div className={css.formRow}>
        {showUSFields && (
          <FieldTextInput
            id={`${fieldId}.state`}
            name="state"
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="billing address-level1"
            label={stateLabel}
            placeholder={statePlaceholder}
            onUnmount={() => form.change('state', undefined)}
          />
        )}

        <FieldSelect
          id={`${fieldId}.country`}
          name="country"
          disabled={disabled}
          className={css.field}
          label={countryLabel}
          validate={countryRequired}
        >
          <option disabled value="">
            {countryPlaceholder}
          </option>
          {countryCodes.map((country) => {
            return (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            );
          })}
        </FieldSelect>
      </div>

      <div className={css.iAmACompanySwitchWrapper}>
        <p>{iAmACompanyLabel}</p>

        <Switch
          value={showCompanySection}
          onClick={() => setShowCompanySection(!showCompanySection)}
        />
      </div>

      {showCompanySection ? (
        <>
          <FieldTextInput
            id={`${fieldId}.companyName`}
            name="companyName"
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="billing company-name"
            label={companyNameLabel}
            placeholder={companyNamePlaceholder}
            validate={companyNameRequired}
            onUnmount={() => form.change('companyName', undefined)}
          />
          
          <FieldTextInput
            id={`${fieldId}.vatNumber`}
            name="vatNumber"
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="billing vat-number"
            label={vatNumberLabel}
            placeholder={vatNumberPlaceholder}
            validate={vatNumberRequired}
            onUnmount={() => form.change('vatNumber', undefined)}
          />

          <div className={css.formRow}>
            <FieldTextInput
              id={`${fieldId}.billingAddressLine1`}
              name="billingAddressLine1"
              disabled={disabled}
              className={css.field}
              type="text"
              autoComplete="billing billing-address-line1"
              label={billingAddressLine1Label}
              placeholder={billingAddressLine1Placeholder}
              validate={billingAddressLine1Required}
              onUnmount={() => form.change('billingAddressLine1', undefined)}
            />
            {showUSFields && (
              <FieldTextInput
                id={`${fieldId}.billingAddressLine2`}
                name="billingAddressLine2"
                disabled={disabled}
                className={css.field}
                type="text"
                autoComplete="billing billing-address-line2"
                label={billingAddressLine2Label}
                placeholder={billingAddressLine2Placeholder}
                onUnmount={() => form.change('billingAddressLine2', undefined)}
              />
            )}
          </div>
          <div className={css.formRow}>
            <FieldTextInput
              id={`${fieldId}.billingPostalCode`}
              name="billingPostalCode"
              disabled={disabled}
              className={css.field}
              type="text"
              autoComplete="billing billing-postal-code"
              label={postalCodeLabel}
              placeholder={postalCodePlaceholder}
              validate={postalCodeRequired}
              onUnmount={() => form.change('billingPostalCode', undefined)}
              onChange={(event) => handleOnChange(event)}
            />

            <FieldTextInput
              id={`${fieldId}.billingCity`}
              name="billingCity"
              disabled={disabled}
              className={css.field}
              type="text"
              autoComplete="billing billing-address-level2"
              label={cityLabel}
              placeholder={cityPlaceholder}
              validate={cityRequired}
              onUnmount={() => form.change('billingCity', undefined)}
            />
          </div>
          <div className={css.formRow}>
            {showUSFields && (
              <FieldTextInput
                id={`${fieldId}.billingState`}
                name="billingState"
                disabled={disabled}
                className={css.field}
                type="text"
                autoComplete="billing billing-address-level1"
                label={stateLabel}
                placeholder={statePlaceholder}
                onUnmount={() => form.change('billingState', undefined)}
              />
            )}

            <FieldSelect
              id={`${fieldId}.billingCountry`}
              name="billingCountry"
              disabled={disabled}
              className={css.field}
              label={countryLabel}
              validate={countryRequired}
            >
              <option disabled value="">
                {countryPlaceholder}
              </option>
              {countryCodes.map((country) => {
                return (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                );
              })}
            </FieldSelect>
          </div>
        </>
      ) : null}
    </div>
  );
};
StripePaymentAddress.defaultProps = {
  country: null,
  disabled: false,
  fieldId: null,
};

StripePaymentAddress.propTypes = {
  country: string,
  disabled: bool,
  form: object.isRequired,
  fieldId: string,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default StripePaymentAddress;
